"use client";

import {
  AnimatePresence,
  type HTMLMotionProps,
  motion,
  useReducedMotion,
  type Variants,
} from "motion/react";
import {
  Children,
  cloneElement,
  forwardRef,
  type HTMLAttributes,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useId,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const springLayout = {
  type: "spring",
  stiffness: 360,
  damping: 32,
  mass: 0.6,
} as const;

const variants: Variants = {
  initial: { opacity: 0, filter: "blur(6px)" },
  animate: { opacity: 1, filter: "blur(0px)" },
  exit: (isActive: boolean) => (!isActive ? { opacity: 0, filter: "blur(6px)" } : {}),
};

const reducedVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: (isActive: boolean) => (!isActive ? { opacity: 0 } : {}),
};

export interface SharedLayoutBackgroundProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  children: ReactNode;
  as?: "div" | "ul";
  pillClassName?: string;
  inset?: number;
  pillContainerClassName?: string;
}

export const SharedLayoutBackground = forwardRef<HTMLElement, SharedLayoutBackgroundProps>(
  function SharedLayoutBackground(
    {
      children,
      as = "div",
      className,
      onMouseLeave,
      pillClassName,
      pillContainerClassName,
      inset = 20,
      ...props
    },
    forwardedRef,
  ) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const uid = useId();
    const reduceMotion = useReducedMotion() ?? false;

    const renderedChildren = Children.toArray(children)
      .filter(isValidElement)
      .map((child, index) => {
        const element = child as ReactElement<{
          className?: string;
          onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
          children?: ReactNode;
        }>;
        const childKey = element.key ? String(element.key) : `item-${index}`;

        return cloneElement(
          element,
          {
            key: childKey,
            className: cn("relative", element.props.className),
            onMouseEnter: (event: MouseEvent<HTMLElement>) => {
              element.props.onMouseEnter?.(event);
              setActiveId(childKey);
            },
          },
          <>
            <AnimatePresence custom={activeId !== null}>
              {activeId !== null ? (
                <motion.div
                  variants={reduceMotion ? reducedVariants : variants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  custom={activeId !== null}
                  data-slot="shared-layout-background-container"
                  className={cn("pointer-events-none absolute inset-y-0", pillContainerClassName)}
                  style={{ left: -inset, right: -inset }}
                >
                  {activeId === childKey ? (
                    <motion.div
                      layoutId={`shared-background-${uid}`}
                      transition={reduceMotion ? { duration: 0 } : springLayout}
                      data-slot="shared-layout-background-pill"
                      className={cn(
                        "pointer-events-none h-full w-full rounded-2xl bg-primary/[0.06]",
                        pillClassName,
                      )}
                    />
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>
            <div className="relative z-10">{element.props.children}</div>
          </>,
        );
      });

    const handleMouseLeave = (event: MouseEvent<HTMLElement>) => {
      setActiveId(null);
      onMouseLeave?.(event);
    };

    if (as === "ul") {
      return (
        <motion.ul
          {...(props as HTMLMotionProps<"ul">)}
          ref={forwardedRef as Ref<HTMLUListElement>}
          layoutRoot
          onMouseLeave={handleMouseLeave}
          className={cn("flex w-full flex-col", className)}
        >
          {renderedChildren}
        </motion.ul>
      );
    }

    return (
      <motion.div
        {...(props as HTMLMotionProps<"div">)}
        ref={forwardedRef as Ref<HTMLDivElement>}
        layoutRoot
        onMouseLeave={handleMouseLeave}
        className={cn("flex w-full flex-col", className)}
      >
        {renderedChildren}
      </motion.div>
    );
  },
);
